/**
 * Return Request Form Component
 * Handles return form submission to Flow Webhooks with dynamic item management
 * Extends WebhookFormBase for common functionality
 */

// webhook-form-base.js loads immediately before this script, but can fail
// independently (CSP, network abort, stale cached bundle), leaving WebhookFormBase
// undefined — which throws at the `extends` clause below and aborts the page script.
// Fall back to a no-op base whose constructor leaves `this.form` null, so the
// `if (!this.form) return;` guard in the constructor bails cleanly and the native
// form still submits. Same pattern as quote-request-form.js (SHOPIFY-1SR).
if (typeof WebhookFormBase === 'undefined') {
  window.WebhookFormBase = class {
    constructor() {
      this.form = null;
    }
  };
}

class ReturnRequestForm extends WebhookFormBase {
  constructor() {
    // Call parent constructor with configuration
    super({
      formId: 'return-request-form',
      submitButtonId: 'ReturnForm-Submit',
      successMessageId: 'ReturnForm-Success',
      errorMessageId: 'ReturnForm-Error',
      errorTextId: 'ReturnForm-ErrorText',
      loadingAnnouncementId: 'ReturnForm-LoadingAnnouncement',
      configKey: 'RETURN_FORM_CONFIG',
      translationsKey: 'RETURN_FORM_TRANSLATIONS'
    });

    // Bail if the base class aborted init (form missing, or WebhookFormUtils /
    // FormUIUtils failed to load — the soft-fail path sets this.disabled). Without
    // the this.disabled check, init below proceeds on a half-constructed object.
    if (!this.form || this.disabled) return;

    // Return-specific elements
    this.orderSelect = document.getElementById('ReturnForm-OrderSelect');
    this.itemsSection = document.getElementById('ReturnForm-ItemsSection');
    this.itemsContainer = document.getElementById('ReturnForm-ItemsContainer');
    this.selectedItemsSection = document.getElementById('ReturnForm-SelectedItems');
    this.selectedItemsContainer = document.getElementById('ReturnForm-SelectedItemsContainer');
    this.addItemButton = document.getElementById('ReturnForm-AddItem');
    this.itemsAnnouncement = document.getElementById('ReturnForm-ItemsAnnouncement');

    // Return-specific state
    this.itemCounter = 0;
    this.availableItems = [];
    this.selectedOrder = null;

    // Performance optimizations
    this.orderDataCache = new Map(); // Cache parsed order data
    this.selectedItemsMap = new Map(); // Track selected items in memory
    this.eventListeners = []; // Track event listeners for cleanup

    // Set translation defaults for return form
    if (!this.translations.product_label) {
      this.translations = {
        product_label: 'Product',
        quantity_label: 'Quantity',
        reason_label: 'Reason for Return',
        remove_button: 'Remove item',
        items_loaded_announcement: 'Loaded {count} items from order. Select items to return.',
        submitting_announcement: 'Submitting return request, please wait...',
        ...this.translations
      };
    }

    this.init();
  }

  /**
   * Initialize the form
   */
  init() {
    try {
      this.bindEvents();
    } catch (error) {
      console.error('Error initializing ReturnRequestForm:', error);
    }
  }

  /**
   * Bind event listeners
   * Extends parent bindEvents() to add return-specific handlers
   */
  bindEvents() {
    // Call parent to bind form submission
    super.bindEvents();

    // Order selection change - use bound method for cleanup
    if (this.orderSelect) {
      const handler = () => this.handleOrderChange();
      this.orderSelect.addEventListener('change', handler);
      this.eventListeners.push({ element: this.orderSelect, event: 'change', handler });
    }

    // Add item button
    if (this.addItemButton) {
      const handler = () => this.addItemRow();
      this.addItemButton.addEventListener('click', handler);
      this.eventListeners.push({ element: this.addItemButton, event: 'click', handler });
    }
  }

  /**
   * Cleanup event listeners and cached data
   */
  destroy() {
    // Guard: eventListeners may not exist if form wasn't found during construction
    if (!this.eventListeners) return;

    // Remove all tracked event listeners
    this.eventListeners.forEach(({ element, event, handler }) => {
      element.removeEventListener(event, handler);
    });
    this.eventListeners = [];

    // Clear caches
    if (this.orderDataCache) {
      this.orderDataCache.clear();
    }
    if (this.selectedItemsMap) {
      this.selectedItemsMap.clear();
    }
  }

  /**
   * Handle order selection change with caching for performance
   */
  handleOrderChange() {
    const selectedOption = this.orderSelect.selectedOptions[0];

    if (!selectedOption || !selectedOption.value) {
      // No order selected - hide items section
      this.itemsSection.style.display = 'none';
      this.itemsContainer.innerHTML = '';
      this.availableItems = [];
      this.selectedOrder = null;
      return;
    }

    const orderId = selectedOption.value;

    // Check cache first - avoid expensive JSON parsing
    if (this.orderDataCache.has(orderId)) {
      const cachedData = this.orderDataCache.get(orderId);
      this.selectedOrder = cachedData.order;
      this.availableItems = cachedData.items;
      this.displayOrderItems();
      return;
    }

    try {
      // Parse order data from option (first time only)
      const lineItemsJson = selectedOption.getAttribute('data-line-items');
      const orderName = selectedOption.getAttribute('data-order-name');
      const orderDate = selectedOption.getAttribute('data-order-date');

      if (!lineItemsJson) {
        console.error('No line items data found for selected order');
        return;
      }

      const lineItems = JSON.parse(lineItemsJson);

      // Store selected order info
      this.selectedOrder = {
        id: orderId,
        name: orderName,
        date: orderDate
      };

      // Store available items (decode HTML entities from Shopify data)
      this.availableItems = lineItems.map(item => ({
        id: item.id || item.variant_id,
        product_id: item.product_id,
        title: this.decodeHtmlEntities(item.title || item.name),
        variant_title: this.decodeHtmlEntities(item.variant_title || ''),
        sku: this.decodeHtmlEntities(item.sku || ''),
        quantity: item.quantity,
        price: item.price,
        image: item.image || item.featured_image?.src || ''
      }));

      // Cache the parsed data for future selections
      this.orderDataCache.set(orderId, {
        order: this.selectedOrder,
        items: this.availableItems
      });

      this.displayOrderItems();
    } catch (error) {
      console.error('Error loading order items:', error);
      this.uiUtils.showMessage('error', 'Failed to load order items. Please try again.');
    }
  }

  /**
   * Display order items (extracted for reuse with caching)
   */
  displayOrderItems() {
    // Clear existing items and selected items
    this.itemsContainer.innerHTML = '';
    this.selectedItemsContainer.innerHTML = '';
    this.selectedItemsMap.clear();
    this.itemCounter = 0;

    // Show items section
    this.itemsSection.style.display = 'block';
    this.selectedItemsSection.style.display = 'none';

    // Announce to screen readers
    if (this.itemsAnnouncement) {
      const message = this.getTranslation('items_loaded_announcement', {
        count: this.availableItems.length
      });
      this.itemsAnnouncement.textContent = message;
    }

    // Display all items
    this.displayAllItems();
  }

  /**
   * Display all order items as visible cards with checkboxes
   * Uses DocumentFragment for optimized batch DOM insertion
   */
  displayAllItems() {
    // Use DocumentFragment for batched DOM operations (massive performance improvement)
    const fragment = document.createDocumentFragment();
    const itemElements = [];

    this.availableItems.forEach((item, index) => {
      const { element, itemId } = this.createItemCard(item, index);
      fragment.appendChild(element);
      itemElements.push(itemId);
    });

    // Single DOM operation - one reflow instead of N reflows
    this.itemsContainer.appendChild(fragment);

    // Bind events after DOM insertion
    itemElements.forEach(itemId => {
      this.bindItemCardEvents(itemId);
    });
  }

  /**
   * Decode HTML entities from Shopify product data
   * Shopify stores product titles with HTML entities like &quot; instead of actual quotes
   * @param {string} text - Text with HTML entities
   * @returns {string} Decoded text
   */
  decodeHtmlEntities(text) {
    if (!text) return '';

    // Create a temporary textarea element to use browser's built-in HTML decoding
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  }

  /**
   * Get return reason options from configuration
   * Falls back to default reasons if not configured
   * @returns {string} HTML options for return reasons
   */
  getReturnReasonOptions() {
    // Get reasons from window config or use fallback defaults
    const reasons = window.RETURN_FORM_REASONS || [
      { value: 'defective', label: 'Defective/Damaged' },
      { value: 'wrong_item', label: 'Wrong Item Received' },
      { value: 'wrong_size', label: 'Wrong Size/Fit' },
      { value: 'changed_mind', label: 'Changed Mind' },
      { value: 'quality', label: 'Quality Issues' },
      { value: 'other', label: 'Other' }
    ];

    return reasons.map(reason =>
      `<option value="${this.escapeAttr(reason.value)}">${this.escapeHtml(reason.label)}</option>`
    ).join('');
  }

  /**
   * Get the human-readable label for a reason code
   * @param {string} reasonCode - The reason code (slug)
   * @returns {string} Human-readable label
   */
  getReasonLabel(reasonCode) {
    const reasons = window.RETURN_FORM_REASONS || [
      { value: 'defective', label: 'Defective/Damaged' },
      { value: 'wrong_item', label: 'Wrong Item Received' },
      { value: 'wrong_size', label: 'Wrong Size/Fit' },
      { value: 'changed_mind', label: 'Changed Mind' },
      { value: 'quality', label: 'Quality Issues' },
      { value: 'other', label: 'Other' }
    ];

    const reason = reasons.find(r => r.value === reasonCode);
    return reason ? reason.label : reasonCode;
  }

  /**
   * Create an item card element (optimized for batch insertion)
   * @param {Object} item - Item data
   * @param {number} index - Item index
   * @returns {Object} { element, itemId }
   */
  createItemCard(item, _index) {
    const itemId = `return-item-${this.itemCounter++}`;

    // Create list item
    const listItem = document.createElement('div');
    listItem.className = 'return-item';
    listItem.id = itemId;
    listItem.setAttribute('data-item-id', item.id);
    listItem.setAttribute('data-product-id', item.product_id);
    listItem.setAttribute('data-max-quantity', item.quantity);

    // Create structure first
    listItem.innerHTML = `
      <label class="return-item__label" for="${itemId}-select">
        <input
          type="checkbox"
          id="${itemId}-select"
          class="return-item__checkbox"
          aria-describedby="${itemId}-details"
        >
        <div class="return-item__content" id="${itemId}-details">
          <div class="return-item__header">
            <div class="return-item__title"></div>
            <div class="return-item__variant"></div>
          </div>
          <div class="return-item__meta">
            <span class="return-item__sku"></span>
            <span class="return-item__qty"></span>
          </div>
        </div>
      </label>

      <input type="hidden" class="return-item-data"
        data-product-id="${item.product_id}"
        data-item-id="${item.id}"
        data-max-quantity="${item.quantity}">
    `;

    // Set text content safely (auto-escapes, no double-encoding)
    const _checkbox = listItem.querySelector('.return-item__checkbox');
    const titleEl = listItem.querySelector('.return-item__title');
    const variantEl = listItem.querySelector('.return-item__variant');
    const skuEl = listItem.querySelector('.return-item__sku');
    const qtyEl = listItem.querySelector('.return-item__qty');
    const dataInput = listItem.querySelector('.return-item-data');

    // Label association is handled by <label for="..."> - no aria-label needed
    if (titleEl) titleEl.textContent = item.title;
    if (variantEl && item.variant_title) {
      variantEl.textContent = item.variant_title;
    } else if (variantEl) {
      variantEl.remove();
    }
    if (skuEl && item.sku) {
      skuEl.textContent = `SKU: ${item.sku}`;
    } else if (skuEl) {
      skuEl.remove();
    }
    if (qtyEl) qtyEl.textContent = `Qty Ordered: ${item.quantity}`;

    // Set data attributes
    if (dataInput) {
      dataInput.setAttribute('data-title', item.title);
      dataInput.setAttribute('data-variant-title', item.variant_title || '');
      dataInput.setAttribute('data-sku', item.sku || '');
    }

    return { element: listItem, itemId };
  }

  /**
   * Legacy method kept for backward compatibility with "Add Item" button
   * This is no longer the primary way items are added
   */
  addItemRow() {
    // Display items as cards when order is selected
    if (this.availableItems.length > 0) {
      this.displayAllItems();
    } else {
      this.uiUtils.showMessage('error', 'Please select an order first.');
    }
  }

  /**
   * Bind events for a specific item with memory tracking for performance
   */
  bindItemCardEvents(itemId) {
    const listItem = document.getElementById(itemId);
    if (!listItem) return;

    const checkbox = listItem.querySelector('.return-item__checkbox');
    const dataInput = listItem.querySelector('.return-item-data');
    if (!checkbox) return;

    const handler = (e) => {
      if (e.target.checked) {
        // Add item to selected items section first
        this.addSelectedItemFields(itemId);

        // Track selected item in memory for fast queries (including fieldsCard reference)
        const fieldsCard = document.getElementById(`${itemId}-fields`);
        this.selectedItemsMap.set(itemId, {
          listItem,
          checkbox,
          dataInput,
          fieldsCard  // Cache fieldsCard reference to avoid repeated getElementById
        });

        listItem.classList.add('return-item--selected');
      } else {
        // Remove from tracking map
        this.selectedItemsMap.delete(itemId);

        // Remove item from selected items section
        this.removeSelectedItemFields(itemId);
        listItem.classList.remove('return-item--selected');
      }

      // Update selected items section visibility
      this.updateSelectedItemsVisibility();
    };

    checkbox.addEventListener('change', handler);
    this.eventListeners.push({ element: checkbox, event: 'change', handler });
  }

  /**
   * Add quantity/reason fields for selected item
   */
  addSelectedItemFields(itemId) {
    const listItem = document.getElementById(itemId);
    if (!listItem) return;

    const dataInput = listItem.querySelector('.return-item-data');
    if (!dataInput) return;

    const title = dataInput.getAttribute('data-title');
    const variantTitle = dataInput.getAttribute('data-variant-title');
    const sku = dataInput.getAttribute('data-sku');
    const maxQty = parseInt(dataInput.getAttribute('data-max-quantity'));

    // Create fields card
    const fieldsCard = document.createElement('div');
    fieldsCard.className = 'return-selected-item';
    fieldsCard.id = `${itemId}-fields`;

    // Create structure first
    fieldsCard.innerHTML = `
      <div class="return-selected-item__header">
        <div class="return-selected-item__title"></div>
        <div class="return-selected-item__variant"></div>
        <div class="return-selected-item__sku"></div>
      </div>
      <div class="return-selected-item__fields">
        <div class="return-selected-item__field">
          <label for="${itemId}-quantity" class="return-selected-item__label">
            ${this.getTranslation('quantity_label')} <span class="required" aria-label="required">*</span>
          </label>
          <input
            id="${itemId}-quantity"
            type="number"
            class="form-control return-item-quantity"
            min="1"
            max="${maxQty}"
            value="${maxQty}"
            required
            aria-required="true"
            aria-describedby="${itemId}-quantity-hint ${itemId}-quantity-error"
            aria-invalid="false"
          >
          <span id="${itemId}-quantity-hint" class="return-selected-item__hint">Max: ${maxQty}</span>
          <span id="${itemId}-quantity-error" class="form-error sr-only" role="alert"></span>
        </div>
        <div class="return-selected-item__field">
          <label for="${itemId}-reason" class="return-selected-item__label">
            ${this.getTranslation('reason_label')} <span class="required" aria-label="required">*</span>
          </label>
          <select
            id="${itemId}-reason"
            class="form-control return-item-reason"
            required
            aria-required="true"
            aria-describedby="${itemId}-reason-error"
            aria-invalid="false"
          >
            <option value="">Select reason...</option>
            ${this.getReturnReasonOptions()}
          </select>
          <span id="${itemId}-reason-error" class="form-error sr-only" role="alert"></span>
        </div>
      </div>
    `;

    // Set text content safely (auto-escapes, no double-encoding)
    const titleEl = fieldsCard.querySelector('.return-selected-item__title');
    const variantEl = fieldsCard.querySelector('.return-selected-item__variant');
    const skuEl = fieldsCard.querySelector('.return-selected-item__sku');

    if (titleEl) titleEl.textContent = title;
    if (variantEl && variantTitle) {
      variantEl.textContent = variantTitle;
    } else if (variantEl) {
      variantEl.remove();
    }
    if (skuEl && sku) {
      skuEl.textContent = `SKU: ${sku}`;
    } else if (skuEl) {
      skuEl.remove();
    }

    this.selectedItemsContainer.appendChild(fieldsCard);
  }

  /**
   * Remove quantity/reason fields for deselected item
   */
  removeSelectedItemFields(itemId) {
    const fieldsCard = document.getElementById(`${itemId}-fields`);
    if (fieldsCard) {
      fieldsCard.remove();
    }
  }

  /**
   * Show/hide selected items section based on whether any items are selected
   * Uses tracked map instead of DOM query for performance
   * Announces selection count changes to screen readers
   */
  updateSelectedItemsVisibility() {
    const count = this.selectedItemsMap.size;

    // Use memory-tracked count instead of expensive DOM query
    if (count > 0) {
      this.selectedItemsSection.style.display = 'block';

      // Announce selection count to screen readers
      this.announceSelectionCount(count);
    } else {
      this.selectedItemsSection.style.display = 'none';

      // Announce no items selected
      if (this.itemsAnnouncement) {
        this.itemsAnnouncement.textContent = 'No items selected for return.';
      }
    }
  }

  /**
   * Announce selection count to screen readers
   * @param {number} count - Number of items selected
   */
  announceSelectionCount(count) {
    if (!this.itemsAnnouncement) return;

    const message = count === 1
      ? '1 item selected for return. Complete the quantity and reason fields below.'
      : `${count} items selected for return. Complete the quantity and reason fields below.`;

    this.itemsAnnouncement.textContent = message;
  }

  /**
   * Legacy method for backward compatibility
   * Bind events for a specific item row (old dropdown-based system)
   */
  bindItemEvents(itemId) {
    const itemRow = document.getElementById(itemId);
    if (!itemRow) return;

    // Product selection change
    const productSelect = itemRow.querySelector('.return-item-product');
    const quantityInput = itemRow.querySelector('.return-item-quantity');

    if (productSelect) {
      productSelect.addEventListener('change', (e) => {
        const selectedOption = e.target.selectedOptions[0];
        const maxQuantity = selectedOption.getAttribute('data-max-quantity') || 1;

        if (quantityInput) {
          quantityInput.max = maxQuantity;
          quantityInput.value = Math.min(parseInt(quantityInput.value) || 1, parseInt(maxQuantity));
          quantityInput.disabled = !selectedOption.value;
        }
      });
    }

    // Remove button
    const removeButton = itemRow.querySelector('.btn-remove');
    if (removeButton) {
      removeButton.addEventListener('click', () => {
        this.removeItemRow(itemId);
      });
    }
  }

  /**
   * Remove an item row
   */
  removeItemRow(itemId) {
    const itemRow = document.getElementById(itemId);
    if (itemRow) {
      itemRow.remove();
    }

    // Check if we have any items left
    const remainingItems = this.itemsContainer.querySelectorAll('.return-item-row');
    if (remainingItems.length === 0) {
      // Add at least one item row
      this.addItemRow();
    }
  }

  /**
   * Validate return items (implements abstract method from WebhookFormBase)
   * Uses tracked items map for optimized performance
   * Enhanced with accessible error announcements
   * @returns {boolean} True if validation passes
   */
  validateCustomFields() {
    // Clear all previous error states
    this.clearValidationErrors();

    // Use memory-tracked map instead of DOM query
    if (this.selectedItemsMap.size === 0) {
      this.uiUtils.showMessage('error', 'Please select at least one item to return.');
      this.announceError('Please select at least one item to return.');
      return false;
    }

    // Validate each selected item has quantity and reason
    let hasErrors = false;
    for (const [itemId, { fieldsCard, dataInput: _dataInput }] of this.selectedItemsMap) {
      // Use cached fieldsCard reference instead of getElementById
      if (!fieldsCard) {
        this.uiUtils.showMessage('error', 'Error loading return details. Please try again.');
        this.announceError('Error loading return details. Please try again.');
        return false;
      }

      const quantityInput = fieldsCard.querySelector('.return-item-quantity');
      const reasonSelect = fieldsCard.querySelector('.return-item-reason');
      const quantityError = fieldsCard.querySelector(`#${itemId}-quantity-error`);
      const reasonError = fieldsCard.querySelector(`#${itemId}-reason-error`);

      // Validate quantity is present and within range
      if (!quantityInput?.value) {
        this.setFieldError(quantityInput, quantityError, 'Quantity is required.');
        hasErrors = true;
      } else {
        const qty = parseInt(quantityInput.value);
        const max = parseInt(quantityInput.max);
        if (qty < 1 || qty > max) {
          this.setFieldError(quantityInput, quantityError, `Quantity must be between 1 and ${max}.`);
          hasErrors = true;
        }
      }

      // Validate reason is selected
      if (!reasonSelect?.value) {
        this.setFieldError(reasonSelect, reasonError, 'Return reason is required.');
        hasErrors = true;
      }
    }

    if (hasErrors) {
      this.uiUtils.showMessage('error', 'Please fix the errors in the form before submitting.');
      this.announceError('Please fix the errors in the form before submitting.');
      return false;
    }

    return true;
  }

  /**
   * Set validation error on a field with accessible announcements
   * @param {HTMLElement} input - The input element with error
   * @param {HTMLElement} errorSpan - The error message span
   * @param {string} message - Error message
   */
  setFieldError(input, errorSpan, message) {
    if (!input || !errorSpan) return;

    // Mark field as invalid
    input.setAttribute('aria-invalid', 'true');
    input.classList.add('form-control--error');

    // Show error message
    errorSpan.textContent = message;
    errorSpan.classList.remove('sr-only');
  }

  /**
   * Clear all validation errors
   * Optimized to only clear errors for selected items (not all fields in DOM)
   */
  clearValidationErrors() {
    // Only clear errors for selected items using tracked map with cached fieldsCard
    for (const [itemId, { fieldsCard }] of this.selectedItemsMap) {
      // Use cached fieldsCard reference instead of getElementById
      if (!fieldsCard) continue;

      // Clear quantity input errors
      const quantityInput = fieldsCard.querySelector('.return-item-quantity');
      if (quantityInput) {
        quantityInput.setAttribute('aria-invalid', 'false');
        quantityInput.classList.remove('form-control--error');
      }

      // Clear reason select errors
      const reasonSelect = fieldsCard.querySelector('.return-item-reason');
      if (reasonSelect) {
        reasonSelect.setAttribute('aria-invalid', 'false');
        reasonSelect.classList.remove('form-control--error');
      }

      // Clear quantity error message
      const quantityError = fieldsCard.querySelector(`#${itemId}-quantity-error`);
      if (quantityError) {
        quantityError.textContent = '';
        quantityError.classList.add('sr-only');
      }

      // Clear reason error message
      const reasonError = fieldsCard.querySelector(`#${itemId}-reason-error`);
      if (reasonError) {
        reasonError.textContent = '';
        reasonError.classList.add('sr-only');
      }
    }
  }

  /**
   * Announce error to screen readers via live region
   * @param {string} message - Error message to announce
   */
  announceError(message) {
    if (this.itemsAnnouncement) {
      this.itemsAnnouncement.textContent = message;
    }
  }

  /**
   * Build webhook payload from form data (implements abstract method from WebhookFormBase)
   * Uses tracked items map for optimized performance
   * @returns {Object} Webhook payload
   */
  buildPayload() {
    const formData = new FormData(this.form);

    // Collect return items using tracked map (no DOM queries needed)
    const items = [];

    for (const [_itemId, { fieldsCard, dataInput }] of this.selectedItemsMap) {
      // Use cached fieldsCard reference instead of getElementById
      if (fieldsCard && dataInput) {
        const quantityInput = fieldsCard.querySelector('.return-item-quantity');
        const reasonSelect = fieldsCard.querySelector('.return-item-reason');

        if (quantityInput && reasonSelect && reasonSelect.value) {
          items.push({
            line_item_id: dataInput.getAttribute('data-item-id'),
            product_id: dataInput.getAttribute('data-product-id'),
            product: dataInput.getAttribute('data-title'),
            variant: dataInput.getAttribute('data-variant-title') || '',
            sku: dataInput.getAttribute('data-sku') || '',
            quantity: parseInt(quantityInput.value, 10),
            reason_code: reasonSelect.value,
            reason_label: this.getReasonLabel(reasonSelect.value)
          });
        }
      }
    }

    return {
      payload: {
        type: 'return_request',
        customer: {
          id: formData.get('customer_id'),
          name: formData.get('customer_name'),
          email: formData.get('customer_email')
        },
        order: {
          id: formData.get('order_id'),
          name: this.selectedOrder?.name || '',
          date: this.selectedOrder?.date || ''
        },
        return_items: items,
        additional_notes: formData.get('additional_notes') || '',
        timestamp: new Date().toISOString()
      }
    };
  }

  /**
   * Reset form after successful submission (implements abstract method from WebhookFormBase)
   */
  resetForm() {
    // Reset order selection
    if (this.orderSelect) {
      this.orderSelect.value = '';
    }

    // Hide and clear items section
    this.itemsSection.style.display = 'none';
    this.itemsContainer.innerHTML = '';
    this.availableItems = [];
    this.selectedOrder = null;
    this.itemCounter = 0;

    // Clear notes
    const notesField = document.getElementById('ReturnForm-Notes');
    if (notesField) {
      notesField.value = '';
    }
  }

  /**
   * Get success message (implements abstract method from WebhookFormBase)
   * @returns {string} Success message
   */
  getSuccessMessage() {
    return 'Return request submitted successfully!';
  }

  /**
   * Get email subject for fallback submission
   * Note: [FORM FALLBACK] prefix is automatically added by showEmailFallback()
   * @returns {string} Email subject line (without fallback prefix)
   */
  getEmailSubject() {
    const orderName = this.selectedOrder?.name || 'Order';
    return `Return Request: ${orderName}`;
  }
}

// Initialize when DOM is ready
let returnFormInstance;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    returnFormInstance = new ReturnRequestForm();
  });
} else {
  returnFormInstance = new ReturnRequestForm();
}

// Cleanup on page unload to prevent memory leaks
window.addEventListener('beforeunload', () => {
  if (returnFormInstance && typeof returnFormInstance.destroy === 'function') {
    returnFormInstance.destroy();
  }
});
